/**
 * Centrale MPN → GTIN (EAN) lookup voor Hikvision producten.
 *
 * Voeg hier nieuwe EAN's toe zodra ze bekend zijn; de catalog past ze
 * automatisch toe op elk Hikvision-product met een matchend MPN,
 * zonder dat je `src/lib/catalog.ts` hoeft aan te raken.
 *
 * Gebruik de exact-officiele MPN (hoofdletters, met "/" of "-" precies
 * zoals op de doos / datasheet).
 */
export const HIKVISION_GTINS: Record<string, string> = {
  "DS-2CD1143G0-I": "6941264092544",
  "DS-2CD1183G2-LIUF": "06936422114695",
  "DS-2CD1383G2-LIUF": "6936422114725",
  "DS-2CD1383G2P-LIUF/SL": "6936422124373",
  "DS-2CD2087G3-LI2UY": "6936422107420",
  "DS-2CD2347G2H-LISU/SL": "6942160434193",
  "DS-2CD2387G2H-LISU/SL": "6942160434100",
  "DS-2CD2387G2H-LIU": "6942160436524",
  "DS-2CD2387G2P-LSU/SL": "6931847112400",
  "DS-KIS603-P": "6931847110420",
  "DS-2DE4225IW-DE": "6931847140458",
  "DS-2DE4425IW-DE": "6954273664817",
  "DS-2SE4C425MWG-E/26": "6931847199630",
  "DS-2SE4C425MWG-E/14": "6931847182526",
  "DS-2SE2C400MWG-E/14": "6936422111106",
};

/** Strip niet-cijfers zodat "06936..." en "6936..." identiek vergeleken worden. */
function digitsOnly(v: string): string {
  return (v ?? "").replace(/\D+/g, "");
}

/** Normaliseert een MPN voor lookup (uppercase, trim). */
export function normalizeMpn(mpn: string | null | undefined): string {
  return (mpn ?? "").trim().toUpperCase();
}

/** Lookup een GTIN voor een Hikvision MPN, of undefined als onbekend. */
export function lookupHikvisionGtin(mpn: string | null | undefined): string | undefined {
  const key = normalizeMpn(mpn);
  if (!key) return undefined;
  return HIKVISION_GTINS[key];
}

/**
 * Valideert GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) of GTIN-14 via de
 * standaard mod-10 checksum. Retourneert `null` bij geldige EAN, anders
 * een duidelijke reden.
 */
export function validateGtinFormat(raw: string): string | null {
  const digits = digitsOnly(raw);
  if (!digits) return "leeg of geen cijfers";
  if (![8, 12, 13, 14].includes(digits.length)) {
    return `ongeldige lengte ${digits.length} (verwacht 8, 12, 13 of 14 cijfers)`;
  }
  const nums = digits.split("").map((d) => Number(d));
  const check = nums.pop()!;
  const reversed = nums.reverse();
  const sum = reversed.reduce((acc, n, i) => acc + n * (i % 2 === 0 ? 3 : 1), 0);
  const expected = (10 - (sum % 10)) % 10;
  if (expected !== check) {
    return `ongeldige checksum (verwacht ${expected}, kreeg ${check})`;
  }
  return null;
}

export type GtinValidationIssue =
  | { type: "invalid_format"; mpn: string; gtin: string; reason: string }
  | { type: "duplicate_gtin"; gtin: string; mpns: string[] };

/**
 * Controleert de volledige HIKVISION_GTINS map op formaatfouten en
 * dubbele EAN's. Retourneert een lijst van issues (leeg = alles ok).
 * Dubbele MPN-keys zijn onmogelijk in een object-literal — die worden al
 * door TypeScript / de JS-runtime samengevoegd.
 */
export function validateHikvisionGtins(
  map: Record<string, string> = HIKVISION_GTINS,
): GtinValidationIssue[] {
  const issues: GtinValidationIssue[] = [];
  const byGtin = new Map<string, string[]>();

  for (const [rawMpn, rawGtin] of Object.entries(map)) {
    const mpn = normalizeMpn(rawMpn);
    const reason = validateGtinFormat(rawGtin);
    if (reason) {
      issues.push({ type: "invalid_format", mpn, gtin: rawGtin, reason });
      continue;
    }
    const norm = digitsOnly(rawGtin);
    const list = byGtin.get(norm) ?? [];
    list.push(mpn);
    byGtin.set(norm, list);
  }

  for (const [gtin, mpns] of byGtin) {
    if (mpns.length > 1) issues.push({ type: "duplicate_gtin", gtin, mpns });
  }
  return issues;
}

/** Menselijk-leesbare regel per issue voor logs / errors. */
export function formatGtinIssue(issue: GtinValidationIssue): string {
  switch (issue.type) {
    case "invalid_format":
      return `❌ ${issue.mpn}: EAN "${issue.gtin}" — ${issue.reason}`;
    case "duplicate_gtin":
      return `❌ Dubbele EAN ${issue.gtin} gebruikt door: ${issue.mpns.join(", ")}`;
  }
}

// Validate at module load — een foutieve of dubbele EAN valt direct op
// tijdens dev/build in plaats van pas bij een klantvraag in productie.
{
  const _issues = validateHikvisionGtins();
  if (_issues.length > 0) {
    const msg = ["HIKVISION_GTINS validatie mislukt:", ..._issues.map(formatGtinIssue)].join("\n");
    if (import.meta.env?.DEV) {
      console.error(msg);
    } else {
      throw new Error(msg);
    }
  }
}

type ProductLike = {
  brand?: string;
  mpn?: string | null;
  gtin?: string | null;
  identifier_exists?: boolean;
};

/**
 * Vult automatisch gtin + identifier_exists op elk Hikvision-product waar
 * we een MPN → GTIN mapping voor kennen. Overschrijft nooit een reeds
 * ingevulde `gtin`.
 */
export function applyHikvisionGtin<T extends ProductLike>(p: T): T {
  if (p.brand !== "Hikvision") return p;
  if (p.gtin) return { ...p, identifier_exists: true };
  const found = lookupHikvisionGtin(p.mpn);
  if (!found) return p;
  return { ...p, gtin: found, identifier_exists: true };
}
